import { createSelector } from 'reselect';
import { getMealDealBuilders } from 'redux/modules/meal-deals/selectors';
import { getWineOfferBuilderItems } from 'redux/modules/entities/selectors/promotions/wine';
import { getOfferBuilderItems } from 'redux/modules/entities/selectors/promotions';
import { BuilderVariant } from './types';

const EMPTY_OBJECT = Object.freeze({});

const selectIndex = (_: WtrState, params: { index: number }) => params.index;
const selectVariant = (_: WtrState, params: { variant: string }) => params.variant;

const getBuilderByIndex = createSelector(
  [
    getMealDealBuilders,
    (state, params) => getWineOfferBuilderItems(state, params.promotionId),
    (state, params) => getOfferBuilderItems(state, params.promotionId),
    selectIndex,
    selectVariant,
  ],
  (mealDealBuilders, wineBuilders, offerBuilders, index, variant) => {
    switch (variant) {
      case 'meal deal':
        return mealDealBuilders?.[index];
      case 'offer':
        return offerBuilders[index];
      case 'wine':
        return wineBuilders[0];
      default:
        return EMPTY_OBJECT;
    }
  },
);

export const getBuilderCompletedByIndex = (
  state: WtrState,
  {
    promotionId,
    index,
    variant,
  }: { index: number; promotionId: string | undefined; variant: BuilderVariant },
) => getBuilderByIndex(state, { promotionId, index, variant })?.completed;

export const getBuilderSavingsByIndex = (
  state: WtrState,
  {
    promotionId,
    index,
    variant,
  }: { index: number; promotionId: string | undefined; variant: BuilderVariant },
) => getBuilderByIndex(state, { promotionId, index, variant })?.savings;

export const getBuilderGroupByIndex = createSelector(
  [getBuilderByIndex, (_state, params) => params.groupIndex],
  (builder, groupIndex) => {
    const group = builder?.groups?.[groupIndex];
    if (!group) return EMPTY_OBJECT;

    return {
      ...group,
      items: group.items?.map((item: { lineNumber: string; quantity: { amount: number } }) =>
        item ? { lineNumber: item.lineNumber, quantity: item.quantity } : null,
      ),
    };
  },
);

export const getBuilderGroupsLength = (
  state: WtrState,
  { promotionId, variant }: { promotionId: string | undefined; variant: BuilderVariant },
) => getBuilderByIndex(state, { promotionId, index: 0, variant })?.groups?.length ?? 0;

export const getGroupNameByIndex = createSelector(
  getBuilderGroupByIndex,
  group => group?.name ?? group?.title,
);
